import {
  DEFAULT_COUNTRY_CODE,
  detectBrowserCountryCode,
  findCountryByDialDigits,
  getPhoneCountry,
  type PhoneCountryConfig,
} from '@/utils/phoneCountries';

export type ParsedPhone = {
  countryCode: string;
  national: string;
  e164: string;
};

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** Strip to national digits and enforce max length for the selected country. */
export function sanitizeNationalInput(countryCode: string, raw: string): string {
  const country = getPhoneCountry(countryCode);
  return digitsOnly(raw).slice(0, country.nationalLength);
}

export function buildE164(countryCode: string, national: string): string {
  const country = getPhoneCountry(countryCode);
  const digits = digitsOnly(national);
  if (!digits) return '';
  if (!country.dialCode) return digits;
  return `+${country.dialCode}${digits}`;
}

export function parsePhoneValue(value: string | undefined | null, defaultCountry: string): ParsedPhone {
  const fallbackCountry = getPhoneCountry(defaultCountry || DEFAULT_COUNTRY_CODE).code;
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return { countryCode: fallbackCountry, national: '', e164: '' };
  }

  const allDigits = digitsOnly(trimmed);

  if (trimmed.startsWith('+')) {
    const matched = findCountryByDialDigits(allDigits);
    if (matched) {
      const national = allDigits.slice(matched.dialCode.length);
      return {
        countryCode: matched.code,
        national: sanitizeNationalInput(matched.code, national),
        e164: buildE164(matched.code, national),
      };
    }
    return {
      countryCode: fallbackCountry,
      national: sanitizeNationalInput(fallbackCountry, allDigits),
      e164: trimmed.startsWith('+') ? `+${allDigits}` : allDigits,
    };
  }

  const country = getPhoneCountry(fallbackCountry);
  const national = sanitizeNationalInput(country.code, allDigits);
  return {
    countryCode: country.code,
    national,
    e164: buildE164(country.code, national),
  };
}

export function validateNationalNumber(countryCode: string, national: string): string | undefined {
  const digits = digitsOnly(national);
  if (!digits) return undefined;

  const country = getPhoneCountry(countryCode);
  return validateForCountry(country, digits);
}

export function validatePhoneValue(
  value: string | undefined | null,
  defaultCountry?: string,
): string | undefined {
  const parsed = parsePhoneValue(value, defaultCountry ?? detectBrowserCountryCode());
  if (!parsed.national) return undefined;
  return validateNationalNumber(parsed.countryCode, parsed.national);
}

function validateForCountry(country: PhoneCountryConfig, digits: string): string | undefined {
  if (digits.length > country.nationalLength) {
    return `Maximum ${country.nationalLength} digits for ${country.name}`;
  }
  if (digits.length < country.minLength) {
    return `Enter at least ${country.minLength} digits`;
  }
  if (country.code === 'IN' && digits.length === country.nationalLength && !/^[6-9]/.test(digits)) {
    return 'Indian mobile numbers start with 6–9';
  }
  return undefined;
}

export function formatPhoneDisplay(value: string | undefined | null, defaultCountry: string): string {
  const parsed = parsePhoneValue(value, defaultCountry);
  if (!parsed.e164) return '—';
  const country = getPhoneCountry(parsed.countryCode);
  return country.dialCode ? `+${country.dialCode} ${parsed.national}` : parsed.national;
}
